import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ExternalLinkIcon, MapPinIcon, BriefcaseIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { MEMBER_PROFILES } from '../data/member-profiles';
import { getMemberPhotoStyle, formatMemberLocation, getTranslatedField, sanitizeSlug } from '../utils/member-helpers';
interface MemberProfilePageProps {
  slug: string;
}
export function MemberProfilePage({
  slug
}: MemberProfilePageProps) {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const safeSlug = sanitizeSlug(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [safeSlug]);
  const member = MEMBER_PROFILES.find(m => m.slug === safeSlug);
  if (!member) {
    return <div className="min-h-screen bg-haa-navy flex flex-col items-center justify-center p-6" data-id="element-3484">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center" data-id="element-3485">
          <h1 className="font-bebas text-5xl text-white tracking-wider mb-4" data-id="element-3486">
            {isEs ? 'Miembro No Encontrado' : 'Member Not Found'}
          </h1>
          <p className="font-nunito text-white/70 mb-8" data-id="element-3487">
            {isEs ? 'El perfil que buscas no existe o ha sido movido.' : 'The profile you are looking for does not exist or has been moved.'}
          </p>
          <a href="#" className="inline-flex items-center gap-2 bg-haa-blue text-white font-nunito font-bold px-6 py-3 rounded-full hover:bg-haa-skyblue transition-colors" data-id="element-3488">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3489" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </motion.div>
      </div>;
  }
  const role = getTranslatedField(member, 'role', isEs);
  const badge = getTranslatedField(member, 'badge', isEs);
  const bio = getTranslatedField(member, 'bio', isEs);
  const industry = getTranslatedField(member, 'industry', isEs);
  const bioParagraphs = bio.split('\n\n').filter(p => p.trim() !== '');
  return <div className="min-h-screen bg-haa-navy text-white font-nunito" data-id="element-3490">
      {/* Header */}
      <header className="w-full px-6 py-6 sm:px-12 flex items-center justify-between border-b border-white/10" data-id="element-3491">
        <a href="#" className="flex items-center gap-3 group" data-id="element-3492">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-3493" />
        </a>
        <div className="flex items-center gap-4" data-id="element-3494">
          {/* Language Toggle */}
          <div className="flex items-center bg-white/10 rounded-full p-0.5 border border-white/15" data-id="element-3495">
            <button onClick={() => setLanguage('EN')} className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wider transition-all duration-200 ${!isEs ? 'bg-haa-skyblue text-white shadow-sm' : 'text-white/60 hover:text-white'}`} data-id="element-3496">
              EN
            </button>
            <button onClick={() => setLanguage('ES')} className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wider transition-all duration-200 ${isEs ? 'bg-haa-skyblue text-white shadow-sm' : 'text-white/60 hover:text-white'}`} data-id="element-3497">
              ES
            </button>
          </div>
          <div className="hidden sm:flex items-center gap-4" data-id="element-3498">
            <a href="#/members" className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-sm transition-colors" data-id="element-3499">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3500" />
              {isEs ? 'Centro de Miembros' : 'Members Hub'}
            </a>
            <span className="text-white/20" data-id="element-3501">|</span>
            <a href="#" className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-sm transition-colors" data-id="element-3502">
              {isEs ? 'Inicio' : 'Home'}
            </a>
          </div>
        </div>
      </header>

      {/* Hero Banner with Flags */}
      <div className="relative overflow-hidden" data-id="element-3503">
        <div className="absolute inset-0" data-id="element-3504">
          <img src="https://cdn.magicpatterns.com/uploads/3P3Acz1M7Egooq4MbdgB9K/iStock-1096894272.jpg" alt="" className="w-full h-full object-cover" data-id="element-3505" />
          <div className="absolute inset-0 bg-haa-navy/85" data-id="element-3506" />
          <div className="absolute inset-0 bg-gradient-to-b from-haa-navy/40 via-transparent to-haa-navy" data-id="element-3507" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 sm:px-12 pt-8 pb-8" data-id="element-3508">
          {/* Breadcrumb */}
          <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex items-center gap-2 text-sm text-white/50 mb-10" data-id="element-3509">
            <a href="#" className="hover:text-white transition-colors" data-id="element-3510">
              {isEs ? 'Inicio' : 'Home'}
            </a>
            <span data-id="element-3511">/</span>
            <a href="#/members" className="hover:text-white transition-colors" data-id="element-3512">
              {isEs ? 'Centro de Miembros' : 'Members Hub'}
            </a>
            <span data-id="element-3513">/</span>
            <span className="text-white/80" data-id="element-3514">{member.name}</span>
          </motion.div>

          {/* Profile Header: Photo + Info side by side */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-start gap-8" data-id="element-3515">
            {/* Photo — 1/4 width */}
            <div className="w-full sm:w-1/4 flex-shrink-0" data-id="element-3516">
              <div className="aspect-square w-full rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 bg-white/5 ring-4 ring-white/5" data-id="element-3517">
                <img src={member.photo} alt={member.name} className="w-full h-full object-cover" style={getMemberPhotoStyle(member.slug, 'profile')} data-id="element-3518" />
              </div>
            </div>

            {/* Info beside photo */}
            <div className="flex-1 flex flex-col justify-center" data-id="element-3519">
              <span className="inline-block px-3 py-1 bg-white/15 border border-white/25 rounded-md text-xs font-bold tracking-widest uppercase text-white mb-3 w-fit backdrop-blur-sm" data-id="element-3520">
                {badge}
              </span>
              <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-none mb-3 text-white drop-shadow-lg" data-id="element-3521">
                {member.name}
              </h1>
              <p className="text-xl text-haa-skyblue font-semibold mb-1 drop-shadow-sm" data-id="element-3522">
                {role}
              </p>
              {member.company && <p className="text-base text-white/70 font-semibold mb-4" data-id="element-3523">
                  {member.company}
                </p>}

              <div className="flex flex-col gap-2" data-id="element-3524">
                <div className="flex items-center gap-2 text-white/80" data-id="element-3525">
                  <MapPinIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-3526" />
                  <span className="text-base" data-id="element-3527">
                    {formatMemberLocation(member.city, member.state)}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-white/80" data-id="element-3528">
                  <BriefcaseIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-3529" />
                  <span className="text-base" data-id="element-3530">{industry}</span>
                </div>
                {member.socialLinks && member.socialLinks.length > 0 && member.socialLinks.map((link, idx) => <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/80 hover:text-haa-skyblue transition-colors group" data-id="element-3531">
                      <ExternalLinkIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-3532" />
                      <span className="text-base group-hover:underline" data-id="element-3533">
                        {link.label}
                      </span>
                    </a>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 sm:px-12 pt-4 pb-8" data-id="element-3534">
        {/* Divider */}
        <div className="h-[2px] w-full bg-white/20 mb-4" data-id="element-3535" />

        {/* Bio */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="space-y-6 text-white/80 text-lg leading-relaxed mb-10" data-id="element-3536">
          {bioParagraphs.map((paragraph, idx) => <p key={idx} data-id="element-3537">{paragraph}</p>)}
        </motion.div>

        {/* Mobile Back Button */}
        <div className="mt-16 sm:hidden" data-id="element-3538">
          <a href="#" className="flex items-center justify-center gap-2 w-full bg-white/10 text-white font-nunito font-bold px-6 py-4 rounded-xl hover:bg-white/20 transition-colors" data-id="element-3539">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3540" />
            {isEs ? 'Volver al Inicio' : 'Back to Home'}
          </a>
        </div>
      </main>
    </div>;
}